import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { rateLabel } from "@/lib/rentals";
import { Home, Plus, MapPin, Flame, CheckCircle2, Clock } from "lucide-react";

export default function RentalsMine() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/rentals/mine").then(r => setItems(r.data || [])).catch(e => setErr(formatErr(e)));
  }, []);

  if (!user) return <div className="max-w-md mx-auto p-10"><p>Please sign in.</p></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10" data-testid="rentals-mine-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Hosting</p>
          <h1 className="h-display text-4xl">Your rental listings.</h1>
        </div>
        <Link to="/rentals/new" className="btn-primary"><Plus size={14}/> List a rental</Link>
      </div>
      {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}

      <div className="grid sm:grid-cols-2 gap-4 mt-7">
        {items === null && <p className="text-[#8A8A8A]">Loading…</p>}
        {items && items.length === 0 && (
          <div className="card p-8 text-center sm:col-span-2">
            <Home size={26} className="mx-auto text-[#0057FF] mb-2"/>
            <p className="text-[#4A4A4A]">No rentals yet — list your first one.</p>
          </div>
        )}
        {(items || []).map(i => (
          <Link to={`/rentals/${i.id}`} key={i.id} className="card overflow-hidden hover:border-[#0A0A0A] transition-colors" data-testid={`mine-rental-${i.id}`}>
            <div className="aspect-[4/3] bg-[#F2F2F0]">
              {i.images?.[0] ? <img src={i.images[0]} alt="" className="w-full h-full object-cover"/> :
                <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]"><Home size={22}/></div>}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className="font-medium truncate flex-1">{i.title}</h3>
                {i.is_boosted && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1"><Flame size={9}/> {i.boost_tier}</span>}
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F2F2F0] text-[#4A4A4A]">{i.status}</span>
              </div>
              <p className="text-sm text-[#4A4A4A] mt-1">{i.category}</p>
              <div className="flex items-center justify-between mt-2">
                <p className="font-medium">{rateLabel(i.available_rates, i.currency)}</p>
                <span className="text-xs text-[#8A8A8A] inline-flex items-center gap-1"><MapPin size={11}/> {i.location || "—"}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function RentalBookings() {
  const { user } = useAuth();
  const [bks, setBks] = useState(null);
  useEffect(() => { api.get("/rentals/bookings").then(r => setBks(r.data || [])).catch(()=>{}); }, []);
  if (!user) return <div className="max-w-md mx-auto p-10"><p>Please sign in.</p></div>;
  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10" data-testid="rental-bookings-page">
      <p className="overline">Renting · You</p>
      <h1 className="h-display text-4xl">Rentals you've booked.</h1>
      <div className="space-y-3 mt-7">
        {bks === null && <p className="text-[#8A8A8A]">Loading…</p>}
        {bks && bks.length === 0 && <div className="card p-8 text-center"><Home size={22} className="mx-auto text-[#0057FF] mb-2"/><p className="text-[#4A4A4A]">No bookings yet.</p></div>}
        {(bks || []).map(b => {
          const meta = b.payment_status === "paid"
            ? { Icon: CheckCircle2, cls: "bg-[#0A0A0A] text-white", label: "Confirmed" }
            : { Icon: Clock, cls: "bg-[#F2F2F0] text-[#4A4A4A]", label: "Pending" };
          const Icon = meta.Icon;
          return (
            <Link to={b.listing ? `/rentals/${b.listing_id}` : "#"} key={b.id} className="card p-5 flex items-center gap-3 hover:border-[#0A0A0A] transition-colors" data-testid={`my-rental-booking-${b.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{b.listing_title || (b.listing?.title) || "(removed)"}</p>
                <p className="text-sm text-[#4A4A4A] mt-0.5">
                  {b.units} {b.unit}{b.units===1?"":"s"} · {new Date(b.start).toLocaleDateString()} → {new Date(b.end).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${meta.cls}`}><Icon size={11}/> {meta.label}</span>
                <p className="text-xs text-[#8A8A8A] mt-2">{b.currency} {b.amount}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
